import { Extension, type Editor, type Range } from "@tiptap/react";
import Suggestion from "@tiptap/suggestion";

/**
 * Notion-style "/" menu.
 *
 * Built on @tiptap/suggestion rather than hand-rolled matching, which handles the
 * parts that are easy to get subtly wrong: tracking the range as the query is
 * typed, only firing at the start of a word, and routing keys to the menu only
 * while it is open.
 *
 * The menu itself is React (see slash-menu.tsx). The bridge is a ref, because the
 * extension is created once when the editor is built while the menu's state
 * changes on every keystroke.
 */

export interface SlashItem {
  title: string;
  /** Matched against the typed query alongside the title. */
  keywords: string[];
  /** Lucide icon name, resolved by the menu. */
  icon: string;
  run: (editor: Editor, range: Range) => void;
}

export const SLASH_ITEMS: SlashItem[] = [
  {
    title: "Text",
    keywords: ["paragraph", "body", "p"],
    icon: "Pilcrow",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).setParagraph().run(),
  },
  {
    title: "Heading 1",
    keywords: ["h1", "title", "large"],
    icon: "Heading1",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).setHeading({ level: 1 }).run(),
  },
  {
    title: "Heading 2",
    keywords: ["h2", "subtitle", "medium"],
    icon: "Heading2",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).setHeading({ level: 2 }).run(),
  },
  {
    title: "Heading 3",
    keywords: ["h3", "small"],
    icon: "Heading3",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).setHeading({ level: 3 }).run(),
  },
  {
    title: "Bulleted list",
    keywords: ["ul", "unordered", "bullet", "point"],
    icon: "List",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).toggleBulletList().run(),
  },
  {
    title: "Numbered list",
    keywords: ["ol", "ordered", "number"],
    icon: "ListOrdered",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
  },
  {
    title: "Table",
    keywords: ["grid", "rows", "columns"],
    icon: "Table",
    run: (editor, range) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run(),
  },
  {
    title: "Quote",
    keywords: ["blockquote", "citation"],
    icon: "Quote",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
  },
  {
    title: "Code block",
    keywords: ["snippet", "pre", "mono"],
    icon: "Code",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
  },
  {
    title: "Divider",
    keywords: ["hr", "rule", "separator", "line"],
    icon: "Minus",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
  },
];

export const filterSlashItems = (query: string): SlashItem[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return SLASH_ITEMS;
  return SLASH_ITEMS.filter(
    (item) =>
      item.title.toLowerCase().includes(needle) ||
      item.keywords.some((keyword) => keyword.startsWith(needle))
  );
};

/** What the React menu needs in order to render itself. */
export interface SlashMenuState {
  items: SlashItem[];
  query: string;
  /** Viewport coordinates of the "/" that opened the menu. */
  rect: { top: number; bottom: number; left: number } | null;
  select: (item: SlashItem) => void;
}

export interface SlashBridge {
  /** Set by the menu so the extension can hand off arrow keys and Enter. */
  onKeyDown?: (event: KeyboardEvent) => boolean;
  setState: (state: SlashMenuState | null) => void;
}

export const createSlashCommand = (bridge: { current: SlashBridge }) =>
  Extension.create({
    name: "analysisSlashCommand",

    addProseMirrorPlugins() {
      return [
        Suggestion<SlashItem>({
          editor: this.editor,
          char: "/",
          // Only at the start of a word, so "and/or" mid-sentence is left alone.
          allowSpaces: false,
          startOfLine: false,
          items: ({ query }) => filterSlashItems(query),

          command: ({ editor, range, props }) => props.run(editor, range),

          render: () => ({
            onStart: (props) => {
              bridge.current.setState({
                items: props.items,
                query: props.query,
                rect: rectOf(props.clientRect),
                select: (item) => props.command(item),
              });
            },
            onUpdate: (props) => {
              bridge.current.setState({
                items: props.items,
                query: props.query,
                rect: rectOf(props.clientRect),
                select: (item) => props.command(item),
              });
            },
            onKeyDown: (props) => {
              if (props.event.key === "Escape") {
                bridge.current.setState(null);
                return true;
              }
              return bridge.current.onKeyDown?.(props.event) ?? false;
            },
            onExit: () => bridge.current.setState(null),
          }),
        }),
      ];
    },
  });

const rectOf = (
  clientRect?: (() => DOMRect | null) | null
): SlashMenuState["rect"] => {
  const rect = clientRect?.();
  if (!rect) return null;
  return { top: rect.top, bottom: rect.bottom, left: rect.left };
};
